import { useEffect, useState } from 'react';
import { useSearchParams, Link } from 'react-router-dom';
import { CheckCircle, Package, MapPin, Phone, ArrowRight, ShoppingBag } from 'lucide-react';
import api from '../../utils/api';
import { useAuth } from '../../context/AuthContext';
import { cldImg } from '../../utils/cloudinary';
import { analyticsPurchase } from '../../utils/analytics';

export default function ThankYouPage() {
  const [searchParams] = useSearchParams();
  const orderId = searchParams.get('orderId');
  const orderNo = searchParams.get('orderNo'); // full order number passed from checkout (works for guest orders too)
  const { user } = useAuth();
  const [order, setOrder] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    if (!orderId) { setLoading(false); return; }
    api.get(`/orders/my/${orderId}`)
      .then(({ data }) => {
        const o = data.order;
        if (o && typeof o.shipping_address === 'string') {
          try { o.shipping_address = JSON.parse(o.shipping_address); } catch {}
        }
        setOrder(o);
        // Purchase backup for logged-in users (checkout already fired it; the
        // orderKey de-dup prevents double-counting). Skip online orders that were
        // never paid — e.g. someone revisits the URL after cancelling.
        if (o && (o.payment_method === 'cod' || o.payment_status === 'paid')) {
          analyticsPurchase({
            orderKey: o.order_number || o.id || orderId,
            items: (o.items || []).map(it => ({
              product_id: it.product_id,
              name: it.snapshot?.name,
              price: it.price,
              qty: it.quantity,
              variant_label: it.snapshot?.variant_label,
            })),
            value: Number(o.total) || 0,
            shipping: Number(o.shipping_charge) || 0,
          });
        }
      })
      .catch(() => {})
      .finally(() => setLoading(false));
  }, [orderId]);

  if (loading) {
    return (
      <div className="min-h-[70vh] flex items-center justify-center">
        <div className="w-10 h-10 border-4 border-brand-yellow border-t-transparent rounded-full animate-spin" />
      </div>
    );
  }

  return (
    <div className="max-w-2xl mx-auto px-4 py-10 md:py-16">
      {/* Success animation */}
      <div className="text-center mb-8">
        <div className="w-24 h-24 bg-green-50 rounded-full flex items-center justify-center mx-auto mb-5">
          <CheckCircle size={52} className="text-green-500" strokeWidth={1.5} />
        </div>
        <h1 className="text-2xl md:text-3xl font-bold text-brand-dark mb-2">Order Placed!</h1>
        <p className="text-gray-500">Thank you for shopping with TheYellove 💛</p>
      </div>

      {/* Order ID card */}
      <div className="bg-brand-yellow-light border border-brand-yellow/30 rounded-2xl px-6 py-5 text-center mb-6">
        <p className="text-sm text-gray-500 mb-1">Your Order ID</p>
        <p className="text-2xl font-bold text-brand-dark tracking-wide">
          {order?.order_number || orderNo || `#${orderId}`}
        </p>
        <p className="text-xs text-gray-400 mt-1">Save this for tracking your order</p>
      </div>

      {/* Order details */}
      {order && (
        <div className="bg-white rounded-2xl border border-gray-100 divide-y divide-gray-50 mb-6">
          {/* Items */}
          <div className="p-5">
            <p className="text-xs font-bold uppercase tracking-widest text-gray-400 mb-3">Items Ordered</p>
            <div className="space-y-3">
              {order.items?.map((item, i) => {
                const snap = typeof item.snapshot === 'string' ? (() => { try { return JSON.parse(item.snapshot); } catch { return {}; } })() : (item.snapshot || {});
                const name = snap.name || snap.product_name || `Product #${item.product_id}`;
                const image = snap.image || snap.image_url || null;
                const variantLabel = snap.variant_label || snap.color || snap.size || null;
                return (
                  <div key={i} className="flex items-center gap-3">
                    <div className="w-12 h-12 rounded-xl overflow-hidden bg-gray-50 flex-shrink-0">
                      {image
                        ? <img src={cldImg(image, 120)} alt={name} className="w-full h-full object-cover" />
                        : <div className="w-full h-full flex items-center justify-center text-xl">🛍️</div>
                      }
                    </div>
                    <div className="flex-1 min-w-0">
                      <p className="text-sm font-semibold text-brand-dark line-clamp-1">{name}</p>
                      {variantLabel && <p className="text-xs text-gray-400">{variantLabel}</p>}
                    </div>
                    <div className="text-right">
                      <p className="text-sm font-bold text-brand-dark">₹{Number(item.price).toLocaleString('en-IN')}</p>
                      <p className="text-xs text-gray-400">×{item.quantity}</p>
                    </div>
                  </div>
                );
              })}
            </div>
          </div>

          {/* Totals */}
          <div className="px-5 py-4 space-y-2 text-sm">
            <div className="flex justify-between text-gray-500">
              <span>Subtotal</span>
              <span>₹{Number(order.subtotal || order.total).toLocaleString('en-IN')}</span>
            </div>
            {order.discount > 0 && (
              <div className="flex justify-between text-green-600">
                <span>Discount</span>
                <span>− ₹{Number(order.discount).toLocaleString('en-IN')}</span>
              </div>
            )}
            <div className="flex justify-between text-gray-500">
              <span>Shipping</span>
              <span className={order.shipping_charge === 0 ? 'text-green-600 font-semibold' : ''}>
                {order.shipping_charge === 0 ? 'FREE' : `₹${order.shipping_charge}`}
              </span>
            </div>
            <div className="flex justify-between font-bold text-brand-dark text-base pt-2 border-t border-gray-100">
              <span>Total Paid</span>
              <span>₹{Number(order.total).toLocaleString('en-IN')}</span>
            </div>
          </div>

          {/* Delivery address */}
          {order.shipping_address && (
            <div className="px-5 py-4">
              <div className="flex items-start gap-3">
                <MapPin size={16} className="text-brand-yellow mt-0.5 flex-shrink-0" />
                <div>
                  <p className="text-xs font-bold uppercase tracking-widest text-gray-400 mb-1">Delivery To</p>
                  <p className="text-sm font-semibold text-brand-dark">{order.shipping_address.name}</p>
                  <p className="text-sm text-gray-500">
                    {order.shipping_address.address_line1}{order.shipping_address.address_line2 ? `, ${order.shipping_address.address_line2}` : ''}, {order.shipping_address.city}, {order.shipping_address.state} – {order.shipping_address.pincode}
                  </p>
                  <p className="text-sm text-gray-400 flex items-center gap-1 mt-0.5">
                    <Phone size={12} /> {order.shipping_address.phone}
                  </p>
                </div>
              </div>
            </div>
          )}
        </div>
      )}

      {/* What's next */}
      <div className="bg-white rounded-2xl border border-gray-100 p-5 mb-8">
        <p className="text-xs font-bold uppercase tracking-widest text-gray-400 mb-4">What Happens Next</p>
        <div className="space-y-3">
          {[
            { icon: '📦', title: 'Order Confirmed', desc: "We've received your order and will start packing it soon." },
            { icon: '🚚', title: 'Out for Delivery', desc: 'Your order will be shipped within 1–2 business days.' },
            { icon: '🎉', title: 'Delivered', desc: 'Estimated delivery in 4–7 business days.' },
          ].map((s, i) => (
            <div key={i} className="flex items-start gap-3">
              <span className="text-xl w-8 text-center">{s.icon}</span>
              <div>
                <p className="text-sm font-semibold text-brand-dark">{s.title}</p>
                <p className="text-xs text-gray-400">{s.desc}</p>
              </div>
            </div>
          ))}
        </div>
      </div>

      {/* CTA buttons */}
      <div className="flex flex-col sm:flex-row gap-3">
        <Link to="/products" className="flex-1 btn-primary py-3.5 rounded-xl font-semibold flex items-center justify-center gap-2">
          <ShoppingBag size={18} />
          Continue Shopping
        </Link>
        {user && (
          <Link to="/account/orders" className="flex-1 py-3.5 rounded-xl font-semibold border-2 border-brand-yellow text-brand-dark flex items-center justify-center gap-2 hover:bg-brand-yellow-light transition-colors">
            My Orders
            <ArrowRight size={18} />
          </Link>
        )}
      </div>

      {/* Track order */}
      <p className="text-center text-sm text-gray-400 mt-5">
        Want to track?{' '}
        <Link to="/track-order" className="text-brand-yellow font-semibold hover:underline">
          Track your order
        </Link>
      </p>
    </div>
  );
}
